import { UIFarePolicy, UIFarePolicyType, UIFareRule, UIPrice } from "@flights/types";
import { pricingUtils } from "@flights/web-api-utils-universal";
import { useMemo } from "react";

const farePolicyKeys = ["change", "cancel"] as const;
const farePolicyTypes: Record<typeof farePolicyKeys[number], UIFarePolicyType[]> = {
  change: ["CHANGE_BEFORE", "CHANGE_AFTER"],
  cancel: ["CANCEL_BEFORE", "CANCEL_AFTER"]
};

export type FarePolicyType = keyof typeof farePolicyTypes;
export type FareFeature =
  | {
      availability: "included" | "excluded";
      policyType: FarePolicyType;
    }
  | {
      availability: "sellable";
      policyType: FarePolicyType;
      fee: UIPrice;
    };

const useFareRulesData = (fareRules: UIFareRule[]) => {
  return useMemo(() => {
    const cancellationPolicy = getFeature(fareRules, "cancel");
    const changePolicy = getFeature(fareRules, "change");

    return [changePolicy, cancellationPolicy].filter((policy) => policy !== undefined);
  }, [fareRules]);
};

const getFeature = (fareRules: UIFareRule[], type: FarePolicyType): FareFeature | undefined => {
  if (fareRules.length === 0) return undefined;

  const hasIncludedPolicy = fareRules.some(({ availablePolicies = [] }) =>
    availablePolicies.some((policy) => isType(policy, type) && !hasFee(policy))
  );

  if (hasIncludedPolicy) {
    return {
      availability: "included",
      policyType: type
    };
  }

  const sellablePolicies = fareRules.map(({ availablePolicies = [] }) =>
    availablePolicies.find((policy) => isType(policy, type) && hasFee(policy))
  );

  const sellableFee = sellablePolicies[0]?.priceBreakdown?.total;

  if (
    sellableFee &&
    sellablePolicies.every(
      (policy) =>
        policy?.priceBreakdown?.total && !pricingUtils.arePricesDifferent(policy.priceBreakdown?.total, sellableFee)
    )
  ) {
    const fee = sellablePolicies[0]?.priceBreakdown?.total as UIPrice;

    return {
      availability: "sellable",
      policyType: type,
      fee
    };
  }

  const isExcluded = fareRules.every(({ unavailablePolicies = [] }) =>
    unavailablePolicies.some((policy) => isType(policy, type))
  );

  if (isExcluded) {
    return {
      availability: "excluded",
      policyType: type
    };
  }

  return undefined;
};

const isType = (policy: UIFarePolicy, type: FarePolicyType) => farePolicyTypes[type].includes(policy.type);

const hasFee = (policy: UIFarePolicy) => {
  const price = policy.priceBreakdown?.total;
  if (!price) return false;
  return pricingUtils.priceToValue(price) > 0;
};

export default useFareRulesData;
